<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import api from '@/services/api.js';
import Swal from 'sweetalert2';
import logo from "@/assets/img/fondotam-blanco.png";

// --- ESTADOS ---
const cargando = ref(false);
const solicitudes = ref([]); // Usuarios inactivos
const mostrarModal = ref(false);
const usuarioSeleccionado = ref(null);

// Listas maestras para los selectores (Todas las gerencias y cargos)
const listas = reactive({
  regionales: [],
  gerencias: [],
  cargos: []
});

// Formulario de asignación
const formulario = reactive({
  regional: '',
  gerencia: '',
  cargo: '',
  rol: 'USUARIO_FINAL'
});

// --- CARGA DE DATOS INICIALES ---
const cargarDatos = async () => {
  cargando.value = true;
  try {
    // 1. Cargamos las solicitudes pendientes reales
    const resPendientes = await api.get('usuarios-pendientes/');
    solicitudes.value = resPendientes.data;

    // 2. Cargamos las jerarquías para el Modal
    const [resReg, resGer, resCar] = await Promise.all([
      api.get('regionales/'),
      api.get('gerencias/'),
      api.get('cargos/')
    ]);
    listas.regionales = resReg.data;
    listas.gerencias = resGer.data;
    listas.cargos = resCar.data;

  } catch (error) {
    console.error("Error cargando datos:", error);
    Swal.fire("Error", "No se pudieron cargar las solicitudes o la jerarquía.", "error");
  } finally {
    cargando.value = false;
  }
};

onMounted(() => {
  cargarDatos();
});

// --- LÓGICA DE FILTROS EN CASCADA PARA EL MODAL ---
const gerenciasFiltradas = computed(() => {
  if (!formulario.regional) return [];
  // Filtramos para que solo muestre las gerencias de la Regional seleccionada
  return listas.gerencias.filter(g => g.regional === formulario.regional);
});

const cargosFiltrados = computed(() => {
  if (!formulario.gerencia) return [];
  // Filtramos para que solo muestre los cargos de la Gerencia seleccionada
  return listas.cargos.filter(c => c.gerencia === formulario.gerencia);
});

// Si cambia la Regional, reseteamos Gerencia y Cargo
const limpiarCascadaRegional = () => {
  formulario.gerencia = '';
  formulario.cargo = '';
};

// Si cambia la Gerencia, reseteamos el Cargo
const limpiarCascadaGerencia = () => {
  formulario.cargo = '';
};

// --- LÓGICA DEL MODAL ---
const abrirModalEvaluacion = (usuario) => {
  usuarioSeleccionado.value = usuario;
  // Reiniciar formulario
  formulario.regional = '';
  formulario.gerencia = '';
  formulario.cargo = '';
  formulario.rol = 'usuario';
  mostrarModal.value = true;
};

const cerrarModal = () => {
  mostrarModal.value = false;
  usuarioSeleccionado.value = null;
};

// --- APROBACIÓN ---
const aprobarAcceso = async () => {
  if (!formulario.regional || !formulario.gerencia || !formulario.cargo) {
    Swal.fire("Atención", "Debe asignar toda la jerarquía al usuario.", "warning");
    return;
  }

  cargando.value = true;
  try {
    const payload = {
      usuario_id: usuarioSeleccionado.value.id,
      ...formulario
    };

    // Llamada al backend para activar al usuario y crearle su perfil
    await api.post('aprobar-usuario/', payload);

    Swal.fire("¡Aprobado!", `Se ha concedido acceso a ${usuarioSeleccionado.value.first_name}.`, "success");
    cerrarModal();

    // Recargar lista para limpiar la bandeja
    cargarDatos();

  } catch (error) {
    console.error("Error al aprobar:", error);
    Swal.fire("Error", "No se pudo procesar la aprobación.", "error");
  } finally {
    cargando.value = false;
  }
};
</script>
<template>
  <div class="container-fluid py-4 app-transition">
    <div class="row">
      <div class="col-12">
        <div class="card border-0 shadow-xl mb-4 border-radius-xl overflow-hidden card-custom">
          <div class="header-tamep px-4 py-3 border-radius-top d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="logo-container-left me-3 p-1 rounded-lg shadow-sm" style="border-radius: 8px; background-color: #ffffff !important;">
                <img :src="logo" class="logo-img p-1" style="max-height: 40px;">
              </div>
              <div class="border-start ps-3 border-white opacity-8">
                <h6 class="mb-0 text-white font-weight-bolder text-uppercase">Bandeja de Solicitudes Pendientes</h6>
                <small class="text-white opacity-8">Aprobación y asignación jerárquica del personal</small>
              </div>
            </div>
            <div class="badge bg-warning text-dark px-3 py-2 animate__animated animate__pulse animate__infinite">
              <i class="fas fa-clock me-1"></i> {{ solicitudes.length }} Pendientes
            </div>
          </div>

          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table align-items-center mb-0 text-sm">
                <thead class="bg-gray-100 text-secondary">
                  <tr>
                    <th class="text-uppercase font-weight-bolder opacity-7">Solicitante</th>
                    <th class="text-uppercase font-weight-bolder opacity-7">Credenciales</th>
                    <th class="text-uppercase font-weight-bolder opacity-7 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="cargando">
                    <td colspan="3" class="text-center py-4">Procesando...</td>
                  </tr>
                  <tr v-else-if="solicitudes.length === 0">
                    <td colspan="3" class="text-center py-5 text-muted">
                      <i class="fas fa-check-double fa-3x mb-3 text-success opacity-5"></i>
                      <h5>Al día</h5>
                      <p>No hay solicitudes de acceso pendientes.</p>
                    </td>
                  </tr>

                  <tr v-for="user in solicitudes" :key="user.id">
                    <td class="px-4">
                      <div class="d-flex align-items-center">
                        <div class="avatar avatar-sm bg-gradient-dark text-white me-3">
                          {{ user.first_name.charAt(0).toUpperCase() }}
                        </div>
                        <div>
                          <h6 class="mb-0 text-sm font-weight-bold">{{ user.first_name }} {{ user.last_name }}</h6>
                          <p class="text-xs text-muted mb-0">Solicitado el: {{ user.fecha }}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0"><i class="fas fa-user-circle me-1"></i> {{ user.username
                      }}</p>
                      <p class="text-xs text-muted mb-0"><i class="fas fa-envelope me-1"></i> {{ user.email }}</p>
                    </td>
                    <td class="text-center">
                      <button @click="abrirModalEvaluacion(user)" class="btn btn-sm btn-outline-primary mb-0">
                        Evaluar Acceso <i class="fas fa-arrow-right ms-1"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="mostrarModal" class="tamep-modal-overlay" @click.self="cerrarModal">
      <div class="tamep-modal-content p-4 animate__animated animate__zoomIn animate__faster">
        <div class="text-center mb-4">
          <div class="icon-shape bg-gradient-success shadow text-center rounded-circle mx-auto mb-3"
            style="width: 60px; height: 60px;">
            <i class="fas fa-user-check text-white fa-2x mt-3"></i>
          </div>
          <h5 class="font-weight-bolder text-dark mb-0">Aprobar Acceso</h5>
          <p class="text-sm text-muted">Asigne los permisos para <strong>{{ usuarioSeleccionado.first_name }} {{
            usuarioSeleccionado.last_name }}</strong></p>
        </div>

        <div class="row g-3">
          <div class="col-12">
            <label class="label-custom">Asignar Regional</label>
            <select v-model="formulario.regional" @change="limpiarCascadaRegional" class="form-select-custom">
              <option value="">Seleccione Regional...</option>
              <option v-for="r in listas.regionales" :key="r.id" :value="r.id">{{ r.nombre }}</option>
            </select>
          </div>

          <div class="col-md-6">
            <label class="label-custom">Unidad / Gerencia</label>
            <select v-model="formulario.gerencia" @change="limpiarCascadaGerencia" class="form-select-custom"
              :disabled="!formulario.regional">
              <option value="">Seleccione...</option>
              <option v-for="g in gerenciasFiltradas" :key="g.id" :value="g.id">{{ g.nombre }}</option>
            </select>
          </div>

          <div class="col-md-6">
            <label class="label-custom">Cargo Funcional</label>
            <select v-model="formulario.cargo" class="form-select-custom" :disabled="!formulario.gerencia">
              <option value="">Seleccione...</option>
              <option v-for="c in cargosFiltrados" :key="c.id" :value="c.id">{{ c.nombre }}</option>
            </select>
          </div>

          <div class="col-12 mt-3">
            <label class="label-custom text-primary"><i class="fas fa-shield-alt me-1"></i> Nivel de Permisos en el
              Sistema</label>
            <select v-model="formulario.rol"
              class="form-select-custom border-primary bg-primary bg-opacity-10 font-weight-bold text-dark">
              <option value="usuario">1. Usuario Final (Solo Lectura y Reporte de Fallas)</option>
              <option value="consultor">2. Consultor (Registro Temporal de Activos - Ventana 30min)</option>
              <option value="admin_regional">3. Administrador Regional (Control solo en su Regional)</option>
              <option value="admin_principal">4. Administrador Principal (Jefe Nacional de Activos)</option>
              <option value="admin_informatico">5. Administrador Informático (TI y Configuración de IA)</option>
            </select>
          </div>
        </div>

        <div class="d-flex mt-4 pt-2">
          <button @click="cerrarModal" class="btn btn-light w-50 me-2 py-3 mb-0">Cancelar</button>
          <button @click="aprobarAcceso" class="btn-tamep-primary w-50 py-3 mb-0" :disabled="cargando">
            <i class="fas" :class="cargando ? 'fa-spinner fa-spin' : 'fa-check'"></i>
            {{ cargando ? ' Procesando...' : ' Aprobar y Activar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* TU CSS (Está perfecto, no lo cambié) */
.header-tamep {
  background: linear-gradient(135deg, #003366 0%, #001d3d 100%);
}

.border-radius-top {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.label-custom {
  font-size: 0.70rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #8392ab;
  margin-bottom: 5px;
  display: block;
}

.form-select-custom {
  display: block;
  width: 100%;
  padding: 0.65rem;
  font-size: 0.85rem;
  border: 1px solid #d2d6da;
  border-radius: 10px;
  outline: none;
  transition: 0.3s;
}

.form-select-custom:focus {
  border-color: #003366;
  box-shadow: 0 0 0 2px rgba(0, 51, 102, 0.1);
}

.form-select-custom:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Nuevo: estilo para disabled */

.btn-tamep-primary {
  background: #003366;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  text-transform: uppercase;
  transition: 0.3s;
}

.btn-tamep-primary:hover:not(:disabled) {
  background: #001d3d;
  transform: translateY(-2px);
}

/* MODAL OVERLAY */
.tamep-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 29, 61, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.tamep-modal-content {
  background: white;
  width: 95%;
  max-width: 550px;
  border-radius: 15px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  transition: background-color 0.3s ease;
}
</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .logo-container-left,
.dark-mode .logo-container-left {
  background-color: #ffffff !important;
}

.dark-version .card-custom,
.dark-mode .card-custom {
  background-color: #1e1e1e !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5) !important;
  border: 1px solid #2d2d2d !important;
}

.dark-version .table,
.dark-mode .table {
  color: #e0e0e0 !important;
}

.dark-version .table thead th,
.dark-mode .table thead th {
  background-color: #2a2a2a !important;
  color: #ffffff !important;
  border-bottom: 1px solid #444 !important;
}

.dark-version .table td,
.dark-mode .table td {
  border-bottom: 1px solid #333 !important;
}

.dark-version .bg-gray-100,
.dark-mode .bg-gray-100 {
  background-color: #2a2a2a !important;
}

/* Modal en Modo Oscuro */
.dark-version .tamep-modal-content,
.dark-mode .tamep-modal-content {
  background-color: #1e1e1e !important;
  border: 1px solid #333 !important;
}

.dark-version .tamep-modal-content h5,
.dark-mode .tamep-modal-content h5 {
  color: #ffffff !important;
}

.dark-version .tamep-modal-content .text-dark,
.dark-mode .tamep-modal-content .text-dark {
  color: #e0e0e0 !important;
}

.dark-version .text-muted,
.dark-mode .text-muted {
  color: #aaaaaa !important;
}

/* Formularios del Modal en Modo Oscuro */
.dark-version .form-select-custom,
.dark-mode .form-select-custom {
  background-color: #2a2a2a !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}

.dark-version .form-select-custom:focus,
.dark-mode .form-select-custom:focus {
  border-color: #4dabf7 !important;
}

.dark-version .form-select-custom:disabled,
.dark-mode .form-select-custom:disabled {
  background-color: #1a1a1a !important;
  border-color: #333 !important;
  color: #666 !important;
}

.dark-version .label-custom,
.dark-mode .label-custom {
  color: #aaaaaa !important;
}
</style>